const VIEW_KEY = 'mx-collection-view';

function publishHeaderHeight() {
  const header = document.querySelector('.header');
  const isSticky = header && getComputedStyle(header).position === 'sticky';
  document.documentElement.style.setProperty(
    '--header-height',
    isSticky ? `${header.offsetHeight}px` : '0px'
  );
}
publishHeaderHeight();
window.addEventListener('resize', publishHeaderHeight);
window.addEventListener('load', publishHeaderHeight);

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.url || window.location.pathname;
    this.debounced = null;

    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onInput);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);

    this.onPopState = () => this.render(window.location.href, false);
    window.addEventListener('popstate', this.onPopState);

    // Desktop groups may render closed; the mobile drawer keeps every group open.
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.onMobileChange = () => this.openGroupsOnMobile();
    this.mobileQuery.addEventListener('change', this.onMobileChange);
    this.openGroupsOnMobile();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this.mobileQuery.removeEventListener('change', this.onMobileChange);
  }

  openGroupsOnMobile() {
    if (!this.mobileQuery.matches) return;
    this.querySelectorAll('details.facets__group').forEach((group) => { group.open = true; });
  }

  // The results swap re-renders the sidebar; keep each group as the shopper left it.
  groupState() {
    const state = new Map();
    this.querySelectorAll('details.facets__group[data-group]').forEach((group) => {
      state.set(group.dataset.group, group.open);
    });
    return state;
  }

  restoreGroups(state) {
    this.querySelectorAll('details.facets__group[data-group]').forEach((group) => {
      if (state.has(group.dataset.group)) group.open = state.get(group.dataset.group);
    });
    this.openGroupsOnMobile();
  }

  onInput = (event) => {
    if (!event.target.closest('form')) return;
    clearTimeout(this.debounced);
    this.debounced = setTimeout(() => this.apply(), 450);
  };

  onSubmit = (event) => {
    event.preventDefault();
    this.apply();
  };

  onClick = (event) => {
    if (event.target.closest('.js-facets-open')) {
      this.classList.add('facets-open');
      const btn = event.target.closest('.js-facets-open');
      btn.setAttribute('aria-expanded', 'true');
      return;
    }
    if (event.target.closest('.js-facets-close')) {
      this.classList.remove('facets-open');
      return;
    }
    if (this.classList.contains('facets-open') && event.target === this) {
      this.classList.remove('facets-open');
      return;
    }
    const link = event.target.closest('.facets__pill, .js-facet-clear, .pagination a');
    if (link && link.href) {
      event.preventDefault();
      const toTop = !!event.target.closest('.pagination');
      this.render(link.href, true, toTop);
    }
  };

  apply() {
    const params = new URLSearchParams();
    const seen = new Set();
    this.querySelectorAll('form').forEach((form) => {
      new FormData(form).forEach((value, key) => {
        if (value === '') return;
        const pair = `${key}=${value}`;
        if (seen.has(pair)) return;
        seen.add(pair);
        params.append(key, value);
      });
    });
    const query = params.toString();
    this.render(query ? `${this.baseUrl}?${query}` : this.baseUrl, true);
  }

  async render(url, push = true, scrollTop = false) {
    this.classList.add('is-loading');
    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const id = `CollectionResults-${this.sectionId}`;
      const fresh = doc.getElementById(id);
      const current = document.getElementById(id);
      if (fresh && current) {
        const groups = this.groupState();
        current.innerHTML = fresh.innerHTML;
        this.restoreGroups(groups);
      }
      if (push) history.pushState({}, '', url);
      if (scrollTop) {
        const top = this.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } catch (e) {
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('facet-filters', FacetFilters);

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.floor = Number(this.dataset.min) || 0;
    this.ceil = Number(this.dataset.max) || 0;
    this.sliders = [...this.querySelectorAll('.facets__price-slider')];
    this.numbers = [...this.querySelectorAll('input[type="number"]')];
    this.fill = this.querySelector('.facets__price-fill');

    this.sliders.forEach((s) => s.addEventListener('input', () => this.fromSlider(s)));
    this.numbers.forEach((n) => n.addEventListener('input', () => this.fromNumber()));
    this.paint();
  }

  pick(list, thumb) { return list.find((el) => el.dataset.thumb === thumb); }

  fromSlider(changed) {
    const minS = this.pick(this.sliders, 'min');
    const maxS = this.pick(this.sliders, 'max');
    let lo = Number(minS.value);
    let hi = Number(maxS.value);
    if (lo > hi) {
      if (changed.dataset.thumb === 'min') { lo = hi; minS.value = lo; }
      else { hi = lo; maxS.value = hi; }
    }
    this.pick(this.numbers, 'min').value = lo > this.floor ? lo : '';
    this.pick(this.numbers, 'max').value = hi < this.ceil ? hi : '';
    this.paint();
  }

  fromNumber() {
    const minN = this.pick(this.numbers, 'min');
    const maxN = this.pick(this.numbers, 'max');
    const lo = minN.value === '' ? this.floor : Number(minN.value);
    const hi = maxN.value === '' ? this.ceil : Number(maxN.value);
    this.pick(this.sliders, 'min').value = lo;
    this.pick(this.sliders, 'max').value = hi;
    this.paint();
  }

  paint() {
    if (!this.fill || !this.ceil) return;
    const lo = Number(this.pick(this.sliders, 'min').value);
    const hi = Number(this.pick(this.sliders, 'max').value);
    const span = this.ceil - this.floor || 1;
    this.fill.style.left = `${((lo - this.floor) / span) * 100}%`;
    this.fill.style.right = `${100 - ((hi - this.floor) / span) * 100}%`;
  }
}
customElements.define('price-range', PriceRange);

class ViewSwitch extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('.view-switch__btn')];
    this.grid = this.closest('.collection-page__results')?.querySelector('.product-grid');
    const saved = localStorage.getItem(VIEW_KEY) || 'grid-view';
    this.set(saved, false);
    this.buttons.forEach((btn) =>
      btn.addEventListener('click', () => this.set(btn.dataset.view, true))
    );
  }

  set(view, persist) {
    if (!this.grid) return;
    this.grid.classList.remove('grid-view', 'list-view');
    this.grid.classList.add(view);
    this.buttons.forEach((btn) =>
      btn.setAttribute('aria-pressed', String(btn.dataset.view === view))
    );
    if (persist) localStorage.setItem(VIEW_KEY, view);
  }
}
customElements.define('view-switch', ViewSwitch);
